/* Widget de retour bêta — bouton flottant discret + modale.
 *
 * - Bouton fixe en bas à gauche (opposé au FAB assistant en bas à droite).
 * - Formulaire minimal : type (bug/amélioration/question/autre), gravité, titre, description.
 * - Auto-remplit page_url avec l'URL courante.
 * - Le POST /feedback est journalisé côté backend pour remontée équipe dev.
 * - Masqué quand l'onboarding wizard bloquant est ouvert.
 */
import React, { useState } from "react";
import { useAuth } from "@/lib/auth";
import { api, formatAxiosError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { MessageSquarePlus, Send } from "lucide-react";
import { toast } from "sonner";

export default function FeedbackWidget() {
  const { user, organizations, activeOrgId } = useAuth();
  const activeOrg = organizations?.find((o) => o.id === activeOrgId);
  const onboardingOpen =
    activeOrg &&
    !activeOrg.is_demo &&
    !activeOrg.onboarding_completed &&
    sessionStorage.getItem("fonbex_onboarding_skipped_org") !== activeOrg.id;

  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("bug");
  const [severity, setSeverity] = useState("medium");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [sending, setSending] = useState(false);

  if (!user || onboardingOpen) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/feedback", {
        kind, severity, title, description,
        page_url: window.location.href,
      });
      toast.success("Merci pour votre retour — l'équipe Fonbex a été notifiée.");
      setOpen(false);
      setTitle(""); setDescription(""); setKind("bug"); setSeverity("medium");
    } catch (err) {
      toast.error(formatAxiosError(err));
    } finally { setSending(false); }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="feedback-widget-btn"
        aria-label="Envoyer un retour"
        title="Envoyer un retour Fonbex"
        className="fixed z-30 flex items-center gap-1.5 rounded-full bg-secondary text-secondary-foreground shadow-md hover:shadow-lg hover:bg-secondary/80 transition px-3 py-2 text-xs font-medium"
        style={{
          left: "max(1rem, env(safe-area-inset-left, 0px) + 0.5rem)",
          bottom: "max(1rem, calc(env(safe-area-inset-bottom, 0px) + 1rem))",
        }}
      >
        <MessageSquarePlus className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">Retour</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="feedback-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquarePlus className="h-5 w-5 text-accent" />
              Envoyer un retour à l&apos;équipe Fonbex
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={kind} onValueChange={setKind}>
                  <SelectTrigger className="mt-1" data-testid="feedback-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bug">Signaler un bug</SelectItem>
                    <SelectItem value="improvement">Proposer une amélioration</SelectItem>
                    <SelectItem value="question">Question</SelectItem>
                    <SelectItem value="other">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Gravité</Label>
                <Select value={severity} onValueChange={setSeverity}>
                  <SelectTrigger className="mt-1" data-testid="feedback-severity"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Mineure</SelectItem>
                    <SelectItem value="medium">Moyenne</SelectItem>
                    <SelectItem value="high">Élevée</SelectItem>
                    <SelectItem value="critical">Critique — bloquante</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Titre</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)}
                     required minLength={3} maxLength={200}
                     placeholder="Résumé en une phrase"
                     data-testid="feedback-title" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Description détaillée</Label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)}
                        required minLength={5} maxLength={5000} rows={5}
                        placeholder="Étapes pour reproduire, comportement attendu, contexte…"
                        data-testid="feedback-description" className="mt-1" />
            </div>
            <div className="text-xs text-muted-foreground">
              Page courante : <code className="font-mono break-all">{window.location.pathname}</code>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={sending} data-testid="feedback-submit">
                <Send className="h-3.5 w-3.5 mr-1.5" />
                {sending ? "Envoi…" : "Envoyer le retour"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
